import { useEffect, useState, useCallback } from "react";
import { api, formatRupiah } from "@/lib/api";
import { PageHeader, Badge, EmptyState } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CurrencyInput } from "@/components/CurrencyInput";
import { Plus, Store, MapPin, X, ChevronRight, Archive, RotateCcw } from "lucide-react";

const inputCls =
  "w-full px-3 py-2 rounded-lg bg-slate-950/60 border border-slate-700 text-white text-sm placeholder:text-slate-600 focus:outline-none focus:border-indigo-500 transition";

const emptyForm = {
  nama_event: "",
  lokasi: "",
  tanggal_mulai: "",
  tanggal_selesai: "",
  biaya_transport: 0,
  biaya_booth: 0,
  biaya_lain: 0,
  status: "active",
  catatan: "",
};

function EventModal({ open, onClose, onSaved }) {
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (open) setForm(emptyForm);
  }, [open]);

  if (!open) return null;

  const save = async () => {
    if (!form.nama_event.trim()) return toast.error("Nama event wajib diisi");
    setSaving(true);
    try {
      await api.post("/events", {
        ...form,
        biaya_transport: Number(form.biaya_transport),
        biaya_booth: Number(form.biaya_booth),
        biaya_lain: Number(form.biaya_lain),
      });
      toast.success("Event dibuat");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal menyimpan");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative z-10 w-full max-w-lg rounded-2xl border border-slate-800 bg-[#131822] p-6 shadow-2xl">
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-heading text-xl font-bold text-white">Buat Event Card Show</h2>
          <button onClick={onClose} className="text-slate-500 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <label className="block text-xs text-slate-400 mb-1.5">Nama Event</label>
            <input data-testid="event-nama-input" className={inputCls} value={form.nama_event} onChange={(e) => set("nama_event", e.target.value)} placeholder="Jakarta Card Show 2026" />
          </div>
          <div className="sm:col-span-2">
            <label className="block text-xs text-slate-400 mb-1.5">Lokasi</label>
            <input className={inputCls} value={form.lokasi} onChange={(e) => set("lokasi", e.target.value)} placeholder="ICE BSD, Tangerang" />
          </div>
          <div>
            <label className="block text-xs text-slate-400 mb-1.5">Tanggal Mulai</label>
            <input type="date" className={inputCls} value={form.tanggal_mulai} onChange={(e) => set("tanggal_mulai", e.target.value)} />
          </div>
          <div>
            <label className="block text-xs text-slate-400 mb-1.5">Tanggal Selesai</label>
            <input type="date" className={inputCls} value={form.tanggal_selesai} onChange={(e) => set("tanggal_selesai", e.target.value)} />
          </div>
          <div>
            <label className="block text-xs text-slate-400 mb-1.5">Biaya Transport</label>
            <CurrencyInput value={form.biaya_transport} onChange={(v) => set("biaya_transport", v)} className={inputCls} data-testid="event-transport-input" />
          </div>
          <div>
            <label className="block text-xs text-slate-400 mb-1.5">Biaya Booth</label>
            <CurrencyInput value={form.biaya_booth} onChange={(v) => set("biaya_booth", v)} className={inputCls} data-testid="event-booth-input" />
          </div>
          <div>
            <label className="block text-xs text-slate-400 mb-1.5">Biaya Lain-lain</label>
            <CurrencyInput value={form.biaya_lain} onChange={(v) => set("biaya_lain", v)} className={inputCls} />
          </div>
          <div>
            <label className="block text-xs text-slate-400 mb-1.5">Status</label>
            <select className={inputCls} value={form.status} onChange={(e) => set("status", e.target.value)}>
              <option value="active">Aktif</option>
              <option value="completed">Selesai</option>
            </select>
          </div>
        </div>
        <div className="flex justify-end gap-3 mt-6">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-slate-400 hover:text-white hover:bg-slate-800 transition">Batal</button>
          <button data-testid="save-event-button" onClick={save} disabled={saving} className="px-5 py-2 rounded-lg text-sm font-semibold bg-indigo-600 hover:bg-indigo-500 text-white transition disabled:opacity-60">
            {saving ? "Menyimpan..." : "Simpan"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Events() {
  const { isAdmin } = useAuth();
  const [events, setEvents] = useState([]);
  const [modal, setModal] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/events");
    setEvents(data);
  }, []);

  const toggleStatus = async (e, ev) => {
    e.preventDefault();
    e.stopPropagation();
    const next = ev.status === "active" ? "completed" : "active";
    const msg = next === "completed"
      ? `Arsipkan "${ev.nama_event}"? Status berubah menjadi Selesai. Semua data penjualan & reimburse tetap tersimpan.`
      : `Aktifkan kembali "${ev.nama_event}"?`;
    if (!window.confirm(msg)) return;
    try {
      await api.patch(`/events/${ev.id}/status`, { status: next });
      toast.success(next === "completed" ? "Event diarsipkan (Selesai)" : "Event diaktifkan kembali");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal mengubah status");
    }
  };

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div>
      <PageHeader
        title="Event Card Show"
        subtitle="Penjualan Event"
        action={
          isAdmin && (
            <button data-testid="add-event-button" onClick={() => setModal(true)} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-semibold transition shadow-lg shadow-indigo-600/30">
              <Plus className="w-4 h-4" /> Buat Event
            </button>
          )
        }
      />

      {events.length === 0 ? (
        <EmptyState icon={Store} title="Belum ada event" subtitle="Buat event card show untuk mencatat penjualan, biaya booth, dan biaya transport." />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="events-list">
          {events.map((ev) => {
            const biaya = (ev.biaya_transport || 0) + (ev.biaya_booth || 0) + (ev.biaya_lain || 0) + (ev.total_reimburse || 0);
            const laba = (ev.total_penjualan || 0) - (ev.total_modal || 0) - biaya;
            return (
              <Link key={ev.id} to={`/events/${ev.id}`} data-testid={`event-card-${ev.id}`} className="group rounded-xl border border-slate-800 bg-[#131822] p-5 hover:border-indigo-500/50 transition-all">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="font-heading text-lg font-bold text-white group-hover:text-indigo-300 transition">{ev.nama_event}</h3>
                    <p className="flex items-center gap-1.5 text-sm text-slate-400 mt-1">
                      <MapPin className="w-3.5 h-3.5" /> {ev.lokasi || "-"}
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <Badge className={ev.status === "active" ? "bg-emerald-500/10 text-emerald-300 border-emerald-500/30" : "bg-slate-500/10 text-slate-400 border-slate-500/30"}>
                      {ev.status === "active" ? "Aktif" : "Selesai"}
                    </Badge>
                    {isAdmin && (
                      <button
                        data-testid={`toggle-status-${ev.id}`}
                        onClick={(e) => toggleStatus(e, ev)}
                        title={ev.status === "active" ? "Arsipkan (Selesai)" : "Aktifkan kembali"}
                        className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-semibold border transition ${ev.status === "active" ? "text-amber-300 border-amber-700/40 hover:bg-amber-900/30" : "text-emerald-300 border-emerald-700/40 hover:bg-emerald-900/30"}`}
                      >
                        {ev.status === "active" ? (<><Archive className="w-3 h-3" /> Arsipkan</>) : (<><RotateCcw className="w-3 h-3" /> Aktifkan</>)}
                      </button>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3 mt-4 pt-4 border-t border-slate-800">
                  <div>
                    <p className="text-[10px] font-mono uppercase text-slate-500">Penjualan</p>
                    <p className="font-mono font-bold text-white text-sm">{formatRupiah(ev.total_penjualan)}</p>
                  </div>
                  <div>
                    <p className="text-[10px] font-mono uppercase text-slate-500">Biaya</p>
                    <p className="font-mono font-bold text-rose-300 text-sm">{formatRupiah(biaya)}</p>
                  </div>
                  <div>
                    <p className="text-[10px] font-mono uppercase text-slate-500">Laba Bersih</p>
                    <p className={`font-mono font-bold text-sm ${laba >= 0 ? "text-emerald-300" : "text-rose-300"}`}>{formatRupiah(laba)}</p>
                  </div>
                </div>
                <div className="flex items-center justify-end mt-3 text-xs text-indigo-400 font-medium">
                  Lihat detail & POS <ChevronRight className="w-3.5 h-3.5" />
                </div>
              </Link>
            );
          })}
        </div>
      )}

      <EventModal open={modal} onClose={() => setModal(false)} onSaved={load} />
    </div>
  );
}
